import appConfig from '../../../../../app.config';
import { useLocation } from 'wouter';
import { t } from 'i18next';

import { formatTime } from '../../../../helpers/format-time';
import { getStationCover } from '../../../../helpers/get-station-cover';
import { useAppSelector } from '../../../../state/hooks';
import { selectStations } from '../../../../state/selectors';
import { Cube } from '../../../../players';
import { useVolumeFooterViewModel } from '../../../../hooks/footers/useVolumeFooterViewModel';
import { useStationPlaybackToggle } from '../../../../hooks/main/useStationPlaybackToggle';

import { Cover, IconButton, PlayButton } from '../partials/UI';
import { GraphicEqIcon, OpenInFullIcon, ThumbDownIcon, ThumbUpIcon, VolumeDownIcon, VolumeOffIcon, VolumeUpIcon } from '../partials/Icons';

export const Volume = ({ playerId, channel }) => {
  const [, navigate] = useLocation();
  const stations = useAppSelector(selectStations);
  const playback = useStationPlaybackToggle(playerId, channel);
  const {
    player,
    connection,
    muted,
    volume,
    progressPercent,
    progressSeconds,
    currentSongEntry: e,
    ratedSongs,
    triggerMuted,
    rate,
    changeVolume,
    postChangeVolume
  } = useVolumeFooterViewModel(playerId, channel);

  if (!player) {
    return <></>;
  }

  const disabled = !connection.available || !player.isOnline;
  const stationId = player.channels[channel]?.stationId;
  const station = stations.find(s => s.id == stationId);
  const canRate = !disabled && !!e && e.playing && !ratedSongs.includes(e.persistentId);
  const zoneName = `${channel} - ${player.channels[channel]?.name || player.name}`;
  const showProgress = player.type != Cube && !!e;
  const VolumeIcon = muted || volume === 0 ? VolumeOffIcon : (volume <= 50 ? VolumeDownIcon : VolumeUpIcon);

  let statusText = null;
  if (!connection.available) {
    statusText = t('volume.no_network').replace(/[|\s]+$/, '');
  } else if (!e) {
    statusText = player.isOnline ? t('volume.not_playing') : t('volume.player_offline');
  }

  return (
    <footer className="shrink-0 min-h-[99px] flex items-center bg-ms-surface dark:bg-ms-sidebar px-3 md:px-5 py-3">
      <div className="w-full grid grid-cols-[1fr_auto] md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1fr)] items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-4 min-w-0">
          <button
            type="button"
            disabled={!stationId}
            onClick={() => stationId && navigate(`/${player.id}/${channel}/${stationId}`)}
            title={t('musicstyling.footer.open_station')}
            className="shrink-0 cursor-pointer disabled:cursor-default"
          >
            <Cover src={station && getStationCover(station)} alt="" icon={GraphicEqIcon} className="w-11 h-11 rounded-md" />
          </button>
          <div className="min-w-0">
            {e ? (
              <>
                <div className="text-base font-bold text-ms-text truncate select-all" title={e.artist}>{e.artist}</div>
                <div className="text-sm text-ms-text truncate select-all" title={e.title}>{e.title}</div>
              </>
            ) : (
              <div className="text-sm text-ms-muted truncate">{statusText}</div>
            )}
          </div>
          {appConfig.features.rating && <div className="hidden sm:flex items-center gap-0.5 shrink-0">
            <IconButton onClick={() => rate(false)} disabled={!canRate} title={t('station.dislike_song')}>
              <ThumbDownIcon className="w-4 h-4" />
            </IconButton>
            <IconButton onClick={() => rate(true)} disabled={!canRate} title={t('station.like_song')}>
              <ThumbUpIcon className="w-4 h-4" />
            </IconButton>
          </div>}
        </div>

        <div className="order-last col-span-2 md:order-none md:col-span-1 flex flex-col items-center gap-1.5 min-w-0">
          {!player.model && (
            <PlayButton
              size="lg"
              playing={playback.isActive}
              disabled={!playback.canToggle}
              onClick={playback.toggle}
              title={playback.isActive ? t('musicstyling.station.stop') : t('musicstyling.station.start')}
            />
          )}
          <div className="w-full max-w-xl flex items-center gap-3 text-sm tabular-nums text-ms-text">
            <span className="w-12 text-right">{showProgress ? formatTime(progressSeconds) : ''}</span>
            <div className="flex-1 h-1 rounded-full bg-ms-border overflow-hidden">
              <div
                className="h-full bg-ms-accent rounded-full"
                style={{ width: `${showProgress ? progressPercent : (e ? 100 : 0)}%`, transition: 'width 1s linear' }}
              />
            </div>
            <span className="w-12">{showProgress && e.duration > 0 ? formatTime(e.duration) : ''}</span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 min-w-0">
          <span className="hidden lg:block text-sm text-ms-text truncate" title={zoneName}>{zoneName}</span>
          <div className={`flex items-center gap-2 app-volume-bar ${disabled || !player.allowVolumeChange ? 'opacity-50' : ''}`}>
            <IconButton
              onClick={triggerMuted}
              disabled={disabled || !player.allowVolumeChange}
              title={muted ? t('musicstyling.footer.unmute') : t('musicstyling.footer.mute')}
            >
              <VolumeIcon className="w-6 h-6" />
            </IconButton>
            {player.allowVolumeChange ? (
              <input
                type="range"
                min={1}
                max={101}
                value={(volume + 1) || 1}
                disabled={disabled}
                onChange={changeVolume}
                onMouseUp={postChangeVolume}
                onTouchEnd={postChangeVolume}
                onKeyUp={postChangeVolume}
                aria-label={t('tour.app-volume-bar.title')}
                title={`${volume}%`}
                className="ms-range w-20 sm:w-28"
                style={{ '--ms-range-fill': `${volume || 0}%` }}
              />
            ) : (
              <span className="text-sm tabular-nums text-ms-muted">{volume}%</span>
            )}
          </div>
          <IconButton
            onClick={() => stationId && navigate(`/${player.id}/${channel}/${stationId}`)}
            disabled={!stationId}
            title={t('musicstyling.footer.open_station')}
            className="max-sm:hidden"
          >
            <OpenInFullIcon className="w-6 h-6" />
          </IconButton>
        </div>
      </div>
    </footer>
  );
};
